import {
  Box,
  Slider as ChakraSlider,
  SliderThumb,
  SliderTrack,
  SliderProps,
  Flex,
  IconButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { 
  IconMinus, 
  IconPlus, 
  IconInfo, 
  IconSpeedStats, 
  IconThrottle, 
  IconBraking, 
  IconHandling, 
  IconFastTurns, 
  IconSlowTurns, 
  IconCooling, 
  IconStability, 
} from 'icons';
import { TunableMetrics } from 'api/generated/graphql';
import CustomTooltip from 'components/Tooltip/CustomTooltip';

interface StatSliderProps extends Omit<SliderProps, 'value' | 'onChange'> {
  minSuggestion?: number;
  maxSuggestion?: number;
  statName: string;
  statField: string;
  value: number | string;
  onChange?: (value: number) => void;
  maxLevel?: number;
  tunableMetrics: TunableMetrics[];
  handleSliderOnChangeEnd: (tunedMetric: {
    name: string;
    val: number;
  }) => Promise<void>;
}

const getTextColor = (value: number) => {
  if (value >= 0 && value <= 3.9) return 'bloodMoon.100';
  if (value >= 6.1 && value <= 10) return 'treasuredTeal.100';
  return 'firstDaffodil.100';
}

export const metricIcon = (metric: string) => {
  switch (metric) {
    case 'speed':
      return <IconSpeedStats w="1.25rem" h="1.25rem" __css={{ path: { fill: 'white.80' } }} />;
    case 'acceleration':
      return <IconThrottle w="1.25rem" h="1.25rem" __css={{ path: { fill: 'white.80' } }} />;
    case 'braking':
      return <IconBraking w="1.25rem" h="1.25rem" __css={{ path: { fill: 'white.80' } }} />;
    case 'handling':
      return <IconHandling w="1.25rem" h="1.25rem" __css={{ path: { fill: 'white.80' } }} />;
    case 'fastTurns':
      return <IconFastTurns w="1.25rem" h="1.25rem" __css={{ path: { fill: 'white.80' } }} />;
    case 'slowTurns':
      return <IconSlowTurns w="1.25rem" h="1.25rem" __css={{ path: { fill: 'white.80' } }} />;
    case 'cooling':
      return <IconCooling w="1.25rem" h="1.25rem" __css={{ path: { fill: 'white.80' } }} />;
    case 'stability':
      return <IconStability w="1.25rem" h="1.25rem" __css={{ path: { fill: 'white.80' } }} />;
    default:
      return null;
  }
}

const tooltipLabels = {
  speed: 'Suggested zone based on chosen tire, track temps, and car/track stats.',
  fastTurns: 'Suggested zone based on chosen tire, wind, and car/track stats.',
  handling: 'Suggested zone based on chosen tire and car/track stats.',
  slowTurns: 'Suggested zone based on chosen tire, track bumps, and car/track stats.',
  cooling: 'Suggested zone based on chosen tire, air temps, speed setup, and car/track stats.',
  braking: 'Suggested zone based on chosen tire and car/track stats.',
  acceleration: 'Suggested zone based on chosen tire and car/track stats.',
  stability: 'Suggested zone based on chosen tire, track grip, and car/track stats.',
}

const StatSlider = ({
  minSuggestion = 0,
  maxSuggestion = 10,
  min = 0,
  max = 10,
  maxLevel = 6,
  handleSliderOnChangeEnd,
  value,
  onChange,
  statName,
  statField,
  ...rest
}: StatSliderProps) => {
  const [localValue, setLocalValue] = useState<number>(Number(value));

  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Calcular a largura e posição da área recomendada
  const suggestionWidth = ((maxSuggestion - minSuggestion) / (max - min)) * 100;
  const suggestionLeft = ((minSuggestion - min) / (max - min)) * 100;

  const isInsideSuggestion =
    localValue >= minSuggestion && localValue <= maxSuggestion; 

  const handleSliderChange = (newValue: number) => {
    setLocalValue(newValue);
    if (onChange) onChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(max, localValue + 0.01);
    const formattedValue = Number(newValue.toFixed(2));
    handleSliderChange(formattedValue);
    handleSliderOnChangeEnd({
      name: statField,
      val: formattedValue,
    });
  };

  const handleDecrement = () => {
    const newValue = Math.max(min, localValue - 0.01);
    const formattedValue = Number(newValue.toFixed(2));
    handleSliderChange(formattedValue);
    handleSliderOnChangeEnd({
      name: statField,
      val: formattedValue,
    });
  };

  useEffect(() => {
    setLocalValue(Number(value));
  }, [value]);

  return (
    <Flex 
      direction="column" 
      width="full"
      background="darkVoid.100"
      p="4"
      borderTop="1.5px solid rgba(255, 255, 255, 0.1)"
      boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)"
      borderRadius="8px"
      gap="8"
    >
      <Flex align="center" mb={2} gap="2">
        <Box>
          {metricIcon(statField)}
        </Box>
        <Text
          fontSize="16px"
          lineHeight="24px"
          fontWeight="400"
          fontFamily="heading"
          textTransform="uppercase"
          color="white.95"
        >
          {statName}
        </Text>
      </Flex>
      <Flex align="center" gap={{ base: 4, md: 10 }}>
        <IconButton
          aria-label="Decrease value"
          icon={<IconMinus w="1rem" h="1rem" />}
          onClick={handleDecrement}
          size="sm"
          variant="ghost"
          background="linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.1) 100%)"
          border="1px solid rgba(255, 255, 255, 0.1)"
          _hover={{ bg: 'whiteAlpha.200' }}
          isDisabled={localValue <= min}
        />
        <Box position="relative" flex={1} alignItems="center" display="flex">
          <CustomTooltip
              label={tooltipLabels[statField as keyof typeof tooltipLabels]}
              isOpen={isOpen}
            >
              <Flex
                position="absolute"
                h="full"
                alignItems="flex-start"
                justifyContent="center"
                style={{
                  width: `${suggestionWidth}%`,
                  height: `42px`,
                  left: `${suggestionLeft}%`,
                }}
                background="linear-gradient(180deg, rgba(0, 255, 232, 0.096) 0%, rgba(0, 255, 232, 0) 100%)"
                borderTopRadius="4px"
                opacity={1}
                top="-6"
                zIndex="150"
                onMouseEnter={onOpen}
                onMouseLeave={onClose}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen();
                }}
              >
                <IconInfo 
                  w="0.75rem"
                  h="0.75rem"
                  mt="0.25rem"
                  __css={{
                    path: {
                      fill: 'rgba(0, 255, 232, 1)',
                    },
                  }}
                />
              </Flex>
            </CustomTooltip>
          <ChakraSlider
            aria-label={`${statName}-slider`}
            min={min}
            max={max}
            step={0.1}
            value={localValue}
            onChange={(value) => setLocalValue(Number(value))}
            onChangeEnd={() => handleSliderOnChangeEnd({
              name: statField,
              val: localValue,
            })}
            focusThumbOnChange={false}
            {...rest}
          >
            {/* Área de sugestão */}
            <SliderTrack
              background="rgba(51, 51, 51, 1)"
              h="0.85rem"
              borderRadius="0.25rem"
              position="relative"
              zIndex="151"
            >
              {localValue > 5 && (
                <Box 
                  position="absolute"
                  top="0"
                  left="50%"
                  right="0"
                  bottom="0"
                  background="linear-gradient(90deg, rgba(15, 64, 59, 0) 0%, #26A69A 100%)"
                />
              )}
              {localValue < 5 && (
                <Box 
                  position="absolute"
                  top="0"
                  left="0"
                  right="50%"
                  bottom="0"
                  background="linear-gradient(90deg, #D83832 0%, rgba(114, 30, 26, 0) 100%)"
                />
              )}
            </SliderTrack>

            <SliderThumb
              w="38px"
              h="38px"
              background={isInsideSuggestion 
                ? "linear-gradient(360deg, rgba(0, 255, 232, 0.2) 0%, rgba(0, 255, 232, 0) 100%), linear-gradient(0deg, #000000, #000000)"
                : "linear-gradient(360deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(0deg, #000000, #000000)"}
              style={{
                backgroundOrigin: 'border-box',
              }}
              border={isInsideSuggestion 
                ? "1.5px solid rgba(0, 255, 232, 0.3)"
                : "1.5px solid rgba(255, 255, 255, 0.1)"}
              borderRadius="8px"
              p="0"
              zIndex="152"
            >
                <Text
                  fontSize="16px"
                  lineHeight="22px"
                  fontWeight="600"
                  color={getTextColor(localValue)}
                >
                  {Number.isInteger(localValue) ? localValue.toString() : localValue.toFixed(2)}
                </Text>
            </SliderThumb>
          </ChakraSlider>
        </Box>
        <IconButton
          aria-label="Increase value"
          icon={<IconPlus w="1rem" h="1rem" />}
          onClick={handleIncrement}
          size="sm"
          variant="ghost"
          background="linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.1) 100%)"
          border="1px solid rgba(255, 255, 255, 0.1)"
          _hover={{ bg: 'whiteAlpha.200' }}
          isDisabled={localValue >= max}
        />
      </Flex>
    </Flex>
  );
};

export default StatSlider; 